import { useState } from 'react';
import { Wifi, WifiOff, RefreshCw, Trash2, CheckCircle, AlertTriangle, KeyRound, ExternalLink } from 'lucide-react';
import type { WalletConnection, WalletEntitlementStatus } from '@/api/wallet';
import { useTranslation } from 'react-i18next';

/** Wallet web app, where Premium is renewed and API access is switched on. */
const WALLET_WEB_APP_URL = 'https://web.budgetbakers.com';

interface WalletConnectionCardProps {
  connection: WalletConnection | null;
  onConnect: (token: string) => void;
  onDisconnect: () => void;
  onSync: () => void;
  onTest: () => void;
  isConnecting: boolean;
  isSyncing: boolean;
  isTesting: boolean;
}

export function WalletConnectionCard({
  connection,
  onConnect,
  onDisconnect,
  onSync,
  onTest,
  isConnecting,
  isSyncing,
  isTesting,
}: WalletConnectionCardProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showUpdateToken, setShowUpdateToken] = useState(false);

  const isConnected = !!connection;
  const hasError = connection?.last_sync_error && !connection.is_active;
  const entitlementStatus: WalletEntitlementStatus | null = connection?.entitlement_status ?? null;

  const entitlementCopy = (status: WalletEntitlementStatus): { title: string; body: string } => {
    switch (status) {
      case 'PREMIUM_REQUIRED':
        return {
          title: t('web:walletConnectionCard.premiumRequiredTitle'),
          body: t('web:walletConnectionCard.premiumRequiredBody'),
        };
      case 'API_CONSENT_REQUIRED':
        return {
          title: t('web:walletConnectionCard.apiConsentRequiredTitle'),
          body: t('web:walletConnectionCard.apiConsentRequiredBody'),
        };
      case 'API_CONSENT_AND_PREMIUM_REQUIRED':
        return {
          title: t('web:walletConnectionCard.apiConsentAndPremiumRequiredTitle'),
          body: t('web:walletConnectionCard.apiConsentAndPremiumRequiredBody'),
        };
    }
  };

  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault();
    if (token.trim()) {
      onConnect(token.trim());
      setToken('');
      setShowUpdateToken(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isConnected ? (
            <Wifi className={`h-5 w-5 ${hasError ? 'text-amber-500' : 'text-green-500'}`} />
          ) : (
            <WifiOff className="h-5 w-5 text-text-quaternary" />
          )}
          <h3 className="text-lg font-semibold text-text-primary">{t('web:walletConnectionCard.walletConnection')}</h3>
        </div>

        {isConnected && (
          <div className="flex items-center gap-2">
            {connection.is_active && (
              <span className="flex items-center gap-1 rounded-full bg-green-50 dark:bg-green-900/30 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
                <CheckCircle className="h-3 w-3" /> {t('expenses:recurring.status.active')}
              </span>
            )}
            {hasError && (
              <span className="flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/30 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" /> {t('common:error.title')}
              </span>
            )}
            {entitlementStatus && (
              <span className="flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/30 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" /> {t('web:walletConnectionCard.paused')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Entitlement banner: the token is fine, the Wallet account is not entitled to the API */}
      {entitlementStatus && (
        <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-800 dark:text-amber-400">
          <p className="font-medium">{entitlementCopy(entitlementStatus).title}</p>
          <p className="mt-1">{entitlementCopy(entitlementStatus).body}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <a
              href={WALLET_WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('web:walletConnectionCard.openWalletWeb')}
            </a>
            <button
              onClick={onTest}
              disabled={isTesting}
              className="flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-amber-700 px-3 py-1.5 text-xs font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 transition-colors"
            >
              {isTesting ? t('web:walletConnectionCard.testing') : t('web:walletConnectionCard.testConnection')}
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {connection?.last_sync_error && (
        <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-800 dark:text-amber-400">
          <p className="font-medium">{t('web:walletConnectionCard.lastSyncError')}</p>
          <p className="mt-1">{connection.last_sync_error}</p>
          {hasError && !showUpdateToken && (
            <button
              onClick={() => setShowUpdateToken(true)}
              className="mt-2 flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition-colors"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {t('web:walletConnectionCard.updateToken')}
            </button>
          )}
        </div>
      )}

      {/* Update token form */}
      {isConnected && showUpdateToken && (
        <form onSubmit={handleConnect} className="mt-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-4 space-y-3">
          <p className="text-sm text-text-secondary">
            {t('web:walletConnectionCard.pasteYourNewApiTokenTo')}
          </p>
          <div className="flex gap-2">
            <input
              type="password"
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder={t('web:walletConnectionCard.pasteYourNewWalletApiToken')}
              className="flex-1 rounded-lg border border-border-secondary bg-surface-primary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <button
              type="submit"
              disabled={!token.trim() || isConnecting}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {isConnecting ? t('web:walletConnectionCard.updating') : t('common:button.update')}
            </button>
            <button
              type="button"
              onClick={() => { setShowUpdateToken(false); setToken(''); }}
              className="rounded-lg border border-border-secondary px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover transition-colors"
            >
              {t('common:button.cancel')}
            </button>
          </div>
        </form>
      )}

      {/* Connected state */}
      {isConnected && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-tertiary">{t('web:walletConnectionCard.connectedSince')}</span>
            <span className="font-medium text-text-primary">{formatDate(connection.created_at)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-tertiary">{t('web:walletConnectionCard.lastSync')}</span>
            <span className="font-medium text-text-primary">{formatDate(connection.last_sync_at)}</span>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={onSync}
              disabled={isSyncing}
              className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? t('web:walletConnectionCard.syncing') : t('web:walletConnectionCard.syncNow')}
            </button>

            <button
              onClick={onTest}
              disabled={isTesting}
              className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
            >
              {isTesting ? t('web:walletConnectionCard.testing') : t('web:walletConnectionCard.testConnection')}
            </button>

            <button
              onClick={() => setShowUpdateToken(v => !v)}
              className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover transition-colors"
            >
              <KeyRound className="h-4 w-4" />
              {t('web:walletConnectionCard.updateToken')}
            </button>

            {showDisconnectConfirm ? (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-text-tertiary">{t('web:walletConnectionCard.sure')}</span>
                <button
                  onClick={() => { onDisconnect(); setShowDisconnectConfirm(false); }}
                  className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
                >
                  {t('web:walletConnectionCard.yesDisconnect')}
                </button>
                <button
                  onClick={() => setShowDisconnectConfirm(false)}
                  className="rounded-lg border border-border-secondary px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover transition-colors"
                >
                  {t('common:button.cancel')}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="flex items-center gap-1.5 ml-auto rounded-lg border border-red-200 dark:border-red-900/50 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                {t('web:walletConnectionCard.disconnect')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Disconnected state — token input form */}
      {!isConnected && (
        <form onSubmit={handleConnect} className="mt-4 space-y-3">
          <p className="text-sm text-text-tertiary">
            {t('web:walletConnectionCard.connectYourWalletByBudgetbakersAccount')}
          </p>
          <div className="flex gap-2">
            <input
              type="password"
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder={t('web:walletConnectionCard.pasteYourWalletApiToken')}
              className="flex-1 rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <button
              type="submit"
              disabled={!token.trim() || isConnecting}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {isConnecting ? t('web:walletConnectionCard.connecting') : t('web:walletConnectionCard.connect')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
